import { Observable, throwError } from 'rxjs';
import { finalize, switchMap, catchError, share, tap } from 'rxjs/operators';

import {
  HttpEvent,
  HttpHandler,
  HttpRequest,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';

import { SN_API_URL } from '@lib/api';
import { FsDataDogRumService } from '@lib/data-dog-rum';

import { SnSessionService, TOKEN_EXPIRE_KEY } from '../services';

@Injectable()
export class SnApiTokenInterceptor implements HttpInterceptor {
  private _refresh$: Observable<string>;
  private _tokenRefreshAttempts = new Map<string, number>();
  private _maxRetries = 3;

  constructor(
    @Inject(SN_API_URL) private readonly _apiUrl: string,
    private readonly _sessionService: SnSessionService,
    private readonly _datadogService: FsDataDogRumService,
  ) {}

  public intercept(request: HttpRequest<any>, handler: HttpHandler): Observable<HttpEvent<any>> {
    // If not api url or refresh token then do usual request
    if (
      (!request.url.startsWith(this._apiUrl) &&
        !(
          localStorage.getItem('LPR_API') && request.url.startsWith(localStorage.getItem('LPR_API'))
        ) &&
        !request.url.startsWith(localStorage.getItem('LISTEN_API')) &&
        !request.url.startsWith(localStorage.getItem('PANIC_API'))) ||
      request.headers.has('X-fs-no-auth')
    ) {
      return handler.handle(this._detachNoAuth(request));
    }

    if (this._sessionService.tokenExpiring && !this._sessionService.tokenRefreshing) {
      const tokenExpiry = localStorage.getItem(TOKEN_EXPIRE_KEY);
      const secondsUntilExpiry = this._getSecondsUntilExpiry(tokenExpiry);

      this._datadogService.addAction('fusus_interceptor_preemptive_refresh', {
        requestUrl: request.url,
        secondsUntilExpiry,
        tokenPreview: this._getTokenPreview(this._sessionService.token),
      });

      return this._handler401Error(request, handler);
    }

    return handler.handle(this._applyToken(request)).pipe(
      tap(() => {
        // Clear retry count on successful response
        this._tokenRefreshAttempts.delete(request.url);
      }),
      catchError((errorResponse) => {
        if (
          errorResponse instanceof HttpErrorResponse &&
          errorResponse.status === 401 &&
          errorResponse.error.message !== 'Your password has expired' &&
          !this._inExcludedRequests(request.url)
        ) {
          const tokenExpiry = localStorage.getItem(TOKEN_EXPIRE_KEY);
          const secondsUntilExpiry = this._getSecondsUntilExpiry(tokenExpiry);

          this._datadogService.addAction('fusus_interceptor_401_detected', {
            requestUrl: request.url,
            errorMessage: errorResponse.error?.message || errorResponse.error?.detail,
            secondsUntilExpiry,
            tokenPreview: this._getTokenPreview(this._sessionService.token),
            tokenWasExpired: this._isTokenExpired(secondsUntilExpiry),
          });

          return this._handler401Error(request, handler);
        }

        return throwError(errorResponse);
      }),
    );
  }

  private _detachNoAuth(request: HttpRequest<any>): HttpRequest<any> {
    return request.clone({
      headers: request.headers.delete('X-fs-no-auth'),
    });
  }

  private _applyToken(request: HttpRequest<any>): HttpRequest<any> {
    const token = this._sessionService.token;

    if (!token) {
      return request;
    }
    if (request.url.startsWith(localStorage.getItem('PANIC_API'))) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }
    return request.clone({
      setHeaders: {
        Authorization: `JWT ${token}`,
      },
    });
  }

  private _handler401Error(
    request: HttpRequest<any>,
    handler: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const requestKey = request.url;
    const attempts = this._tokenRefreshAttempts.get(requestKey) || 0;

    if (attempts >= this._maxRetries) {
      this._datadogService.addAction('fusus_interceptor_max_retries_reached', {
        requestUrl: request.url,
        attempts,
      });

      this._tokenRefreshAttempts.delete(requestKey);
      return throwError(
        new HttpErrorResponse({
          status: 401,
          statusText: 'Unauthorized after max refresh attempts',
        }),
      );
    }

    this._tokenRefreshAttempts.set(requestKey, attempts + 1);

    if (this._refresh$) {
      this._datadogService.addAction('fusus_interceptor_joining_existing_refresh', {
        requestUrl: request.url,
        attempt: attempts + 1,
      });

      return this._refresh$.pipe(switchMap(() => this.intercept(request, handler)));
    }

    this._datadogService.addAction('fusus_interceptor_starting_refresh', {
      requestUrl: request.url,
      attempt: attempts + 1,
    });

    this._refresh$ = this._sessionService.refresh().pipe(
      finalize(() => (this._refresh$ = null)),
      share(),
    );

    return this._refresh$.pipe(switchMap(() => this.intercept(request, handler)));
  }

  private _getTokenPreview(token: string | null | undefined): string | undefined {
    return token?.slice(-4);
  }

  private _getSecondsUntilExpiry(tokenExpiry: string | null): number | null {
    if (!tokenExpiry) return null;
    const expiry = +tokenExpiry;
    return !isNaN(expiry) ? Math.floor(expiry - Date.now() / 1000) : null;
  }

  private _isTokenExpired(secondsUntilExpiry: number | null): boolean {
    return secondsUntilExpiry !== null && secondsUntilExpiry < 0;
  }

  private _inExcludedRequests(url: string): boolean {
    const excludedPatterns = [
      'api/integrations/axon/connect/oauth/userinfo',
      'api/users/*/mfa/',
      'api/auth/password',
    ];

    return excludedPatterns.some((pattern) => {
      if (pattern.includes('*')) {
        const regexPattern = pattern.replace(/\*/g, '[^/]+');
        const regex = new RegExp(regexPattern);
        return regex.test(url);
      }
      return url.includes(pattern);
    });
  }
}
